import React, { useState, useContext, useEffect, createRef, useRef } from 'react';
import QuoteBuilderList from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderList.jsx';
import { systemItemsFilter } from 'components/frank/utils/materialCompatibility.js';
import useCommitOnPause from 'components/common/useCommitOnPause';
import FormFieldFactory from './FormFieldFactory.jsx';
import PricingThresholdCard from './form_fields/PricingThresholdCard.jsx';
import FormTitle from './FormTitle.jsx';
import ResultsTable from './ResultsTable.jsx';
import FormActions from './FormActions.jsx';
import MessageAlerts from './MessageAlerts.jsx';
import SystemCheckbox from './SystemCheckbox.jsx';
import { PostRequest as OriginalPostRequest, GetRequest, Tooltip } from 'components/common';
import { applySaveValueInto, seedSaveValueIntoFields } from 'components/common/saveValueInto';
import { attributeProperty } from 'components/common/attributeProperty';
import { Form, Button } from 'semantic-ui-react';
import { useForm  } from "react-hook-form";
import hash from "object-hash";
import { useCache } from './CacheContext';
import RequestQueueManager from 'components/layout_and_quote_builder/utils/RequestQueueManager.js';

// Create a dedicated queue manager with concurrency = 1 for this component
const quoteBuilderQueueManager = new RequestQueueManager(1);

// QueuedPostRequest wrapper using the dedicated queue manager
// PostRequest now returns a Promise, so we just wrap it and enqueue it
const QueuedPostRequest = (props) => {
  const requestFn = () => OriginalPostRequest(props);
  return quoteBuilderQueueManager.enqueue(requestFn);
};

// The section calculator returns the recomputed automatic fire adeti of the priced (or cleared) section and
// any section that follows it (a corrugated follower inherits its carton_lamination leader's die-cutting only --
// rule 8). Push each count to its (read-only, automatic) wastage field so it updates without a reload.
// Applies on the success AND the error responses: unselecting a machine clears the section (an
// incomplete/invalid response) yet still lowers the total, so the field must drop the removed spoilage too.
export const pushNavSectionAutoWastages = (data, emit) => {
  if(Array.isArray(data.nav_section_auto_wastages)) {
    data.nav_section_auto_wastages.forEach((update) => {
      emit("nav_section_wastage_recomputed", {
        label: update.label,
        wastage_count: update.wastage_count
      });
    });
  }

  // The material fire adeti automatic total + per-contributor breakdown for the same sections -- pushed to the
  // material fire adeti field so its (read-only, automatic) value and its badge row update without a reload.
  if(Array.isArray(data.material_auto_wastages)) {
    data.material_auto_wastages.forEach((update) => {
      emit("material_wastage_recomputed", {
        label: update.label,
        column: update.column,
        column_key: update.column_key,
        total: update.total,
        breakdown: update.breakdown
      });
      // Also fold the update into window.material_auto_wastages -- the seed a material field reads on MOUNT. The event
      // above only reaches a field already mounted; a hard_cardboard board consuming another tab's coating labor (rule
      // 9) is usually on an unopened tab, so without this its field would seed the stale page-load value when the user
      // finally opens it. Keyed by nav label + column_key (the column identity that survives a shared column title).
      if(typeof window !== 'undefined') {
        if(!Array.isArray(window.material_auto_wastages)) { window.material_auto_wastages = []; }
        const index = window.material_auto_wastages.findIndex(
          (row) => row.label === update.label && (row.column_key || '') === (update.column_key || '')
        );
        if(index >= 0) { window.material_auto_wastages[index] = update; } else { window.material_auto_wastages.push(update); }
      }
    });
  }
};

// The part types that are printed -- mirrors the server's UserQuoteNavSection::PART_TYPES_PRINTED_BY_DEFAULT
// (the keys of Frank::ManufacturingLineFinder::PRINTING_TYPES_BY_PART_TYPE).
const PART_TYPES_PRINTED_BY_DEFAULT = ['carton', 'carton_lamination', 'cover', 'corrugated_cardboard_offset', 'corrugated_cardboard_flexo', 'corrugated_cardboard_flexo_on_carton'];

// SINGLE SOURCE OF TRUTH for a nav section's default values -- what windowNavSection() falls back to when
// window.nav_sections carries no entry for this section (or none has loaded yet), so the three fallback
// branches below can't drift apart. `lamination` and `printing_side` mirror the server's
// UserQuoteNavSection.default_lamination_for / default_printing_side_for: a carton_lamination section is always
// laminated, and the printed part types default to a one_side printing -- keep the two in sync.
const navSectionDefaults = (partType) => ({
  lamination: partType === 'carton_lamination' ? 'one_side' : 'none',
  printing_side: PART_TYPES_PRINTED_BY_DEFAULT.includes(partType) ? 'one_side' : 'none',
  printing_block: 'one_block',
  wastage_unit: 'sheet',
  wastage_count: 0
});

// Fill any field a resolved nav section is missing from navSectionDefaults (part-type aware), in place.
// `== null` catches both an absent field (undefined) and a column the server serialized as null -- a legacy
// nav section created before a field existed comes over with that field null, and would keep the null (never
// falling back to its default) under a strict `=== undefined` check.
const applyNavSectionDefaults = (navSection) => {
  const defaults = navSectionDefaults(navSection.part_type);
  Object.keys(defaults).forEach((key) => {
    if(navSection[key] == null) { navSection[key] = defaults[key]; }
  });
  return navSection;
};

const QuoteBuilderForm = ({ config, readOnly, disabled, disabledReason, showingState, linkToSettings, beforeSave, afterSave, afterCancel, editStateId, editedKlass, options, readOnlyValues, section_uuid, item, extra_title, navSection, columnTitle, colIndex, rowIndex, getExtraItems, navSectionColumnType, context, hideDeleteFromList, onResultAvailabilityChange }) => {
  const default_fetch_path = '/quote-builder/user_unit_prices/:id';
  const default_fetch_system_path = '/quote-builder/user_unit_prices_system/:id';
  const default_update_path = '/quote-builder/user_unit_prices/update';
  const default_create_path = '/quote-builder/user_unit_prices/create/:key'

  // A UserUnitPriceAttribute reference field's matching machine TAB config (by key), if this form has one -- the
  // SAME config the settings machine tab hands the library picker (library_enabled, listing_properties, the type
  // and dimension filters, the picker title). The field uses it so "create new" opens the full library picker,
  // rather than a hand-copied subset that drops the facets. Undefined for a form/field with no such tab (materials
  // etc.), which then keep the plain manual-create flow. `config` here is the FORM config (its tabs), not a field.
  const attributeTabConfigFor = (fieldKey) => (config.tabs || []).find(
    (tab) => tab.form_type === 'UserUnitPriceAttribute' && tab.key === fieldKey
  );

  const USER_UNIT_PRICE_CHANGED_WITH_ITEM_EVENT_KEY = 'quote_builder.user_unit_price_changed_with_item';

  const STATE_LIST = 'list';
  const STATE_NEW  = 'new';
  const STATE_EDIT = 'edit';
  const STATE_COPY_FROM_SYSTEM  = 'copy_from_system';
  const VALUE_NOT_AVAILABLE = 'not-available'
  const VALUE_NEW = "new-attribute";
  const keyFor = (config) => hash(config);
  const v = (key) => { return values[key] }
  const va = (key) => { return values['user_unit_price_attribute.' + key] }

  // Registry of loaded UserUnitPriceAttributes (machines, ...) by id; each picker reports its list
  // up via onAttributesLoaded so settings.yml expressions can read a SELECTED attribute's property
  // (not just its id, which is all va() gives). Mirrors components/quote_builder/QuoteBuilderForm.
  // useRef, so populating it does NOT trigger a re-render: fine for the current vap() user (a hidden
  // field evaluated fresh at save), but a *visible* field keyed on vap() would not re-render when the
  // picker list finishes loading until the next unrelated render.
  const attributesById = useRef({});
  const registerAttributeProperties = (loadedAttributes) => {
    (loadedAttributes || []).forEach((attribute) => {
      if (attribute && attribute.id != undefined) { attributesById.current[attribute.id] = attribute; }
    });
  };
  // makeVap(bag): build vap('printing_machine', 'printing_type') bound to a value bag, so a visible_if
  // resolves the SELECTED attribute's property from the same bag it is evaluated against — live values
  // on save, the freshly fetched record on seed. See components/common/attributeProperty.js.
  const makeVap = (bag) => (attributeKey, propertyName) => attributeProperty(bag, attributesById.current, attributeKey, propertyName);
  // vap resolves against the live `values`, read LAZILY at call time (values is declared further below):
  // binding eagerly here would hit a temporal-dead-zone ReferenceError under strict const semantics
  // (jest) and capture an undefined `values` where const is transpiled to var (the browser build).
  // Passed to FormFieldFactory for the field-render visible_if evals.
  const vap = (attributeKey, propertyName) => makeVap(values)(attributeKey, propertyName);
  const onlyUnique = (value, index, self) => { return self.indexOf(value) === index; }

  const is_any_new_value = (list) => { return list.indexOf(VALUE_NEW) > -1 }
  const is_not_new_value = (list) => { return list.indexOf(VALUE_NEW) == -1 }
  const is_not_new_value_va = (list) => { return is_not_new_value(list.map((key) => va(key))) }

  // Use cache context
  const { state: cacheState, dispatch: cacheDispatch } = useCache();

  const [submitInProgress, setSubmitInProgress] = useState(false);
  // const [hasFile, setHasFile] = useState('');
  const has_file = useRef('');
  const zeroPriceValueFor = (attributeName) => {
    const attributeConfig = config.attributes.find((candidate) => candidate.name === attributeName);
    const precision = attributeConfig != undefined && attributeConfig.precision != undefined ? attributeConfig.precision : 2;
    return (0).toFixed(precision);
  };

  // A multi-select (checkboxes) attribute added after a record was created is absent from that
  // record and arrives null from the server. Seed its default_value (typically all options) so it
  // renders like a new record rather than empty; an explicit saved selection arrives as an array
  // (even an empty one) and is used as-is, so unchecking and saving still sticks.
  const multiSelectDefaultFor = (attributeName) => {
    const attributeConfig = config.attributes.find((candidate) => candidate.name === attributeName);
    if (attributeConfig != undefined && attributeConfig.form_type === 'checkboxes' && attributeConfig.default_value != undefined) {
      return attributeConfig.default_value;
    }
    return undefined;
  };

  // A field that arrives null from the server (unset on this record -- e.g. a wastage count never
  // entered) and that declares a default_value must not be pushed into the input as raw null: React
  // IGNORES value={null} on a controlled input, leaving whatever the PREVIOUSLY edited record
  // displayed (the reported bug -- switching prices left the wastage field stuck on the prior
  // record's value). Seed the field's declared default instead -- a real value the input renders,
  // and the field's own intended "empty" (0 for the wastage counts). Fields with no declared default
  // are left as the server sent them.
  const configuredDefaultFor = (attributeName) => {
    const attributeConfig = config.attributes.find((candidate) => candidate.name === attributeName);
    return attributeConfig != undefined ? attributeConfig.default_value : undefined;
  };

  const defaultValueByFormType = (config) => {
    if(config.form_type == 'price' && config.precision == 4) { return '0.0000' }

    return {
      'UserUnitPriceAttribute': '',
      'UserUnitPrice': 'not-available',
      'select': '',
      'checkboxes': [],
      'boolean': false,
      'hidden': '',
      'length': '0.00',
      'number': '0.00',
      'integer': '0',
      'price': '0.00',
      'price_with_precision_4': '0.0000',
      'currency': (window.user_quote && window.user_quote.currency) || window.default_currency
    }[config.form_type]
  };


  const windowNavSection = () => {
    // Guard against undefined navSection prop -- no navSection means no part_type to derive from, so
    // lamination falls to "none" (navSectionDefaults with no part type).
    if (!navSection) {
      console.warn('[QuoteBuilderForm] navSection prop is undefined');
      return navSectionDefaults();
    }

    // Guard against undefined window.nav_sections
    if (!window.nav_sections || !Array.isArray(window.nav_sections)) {
      console.warn('[QuoteBuilderForm] window.nav_sections is not available, using navSection prop');
      return applyNavSectionDefaults({ ...navSection });
    }

    let result = undefined;

    // Try 1: Match by index/sort_order (primary method after Layer 1 fix)
    if(navSection.index !== undefined) {
      for(let i = 0; i < window.nav_sections.length; i++) {
        if(window.nav_sections[i].sort_order === navSection.index ||
           window.nav_sections[i].index === navSection.index) {
          result = window.nav_sections[i];
          break;
        }
      }
    }

    // Try 2: If index doesn't match, try label (fallback for stale props)
    if(result === undefined && navSection.label !== undefined) {
      for(let i = 0; i < window.nav_sections.length; i++) {
        if(window.nav_sections[i].label === navSection.label) {
          result = window.nav_sections[i];
          console.warn(`[QuoteBuilderForm] navSection matched by label instead of index (${navSection.index}). This may indicate stale props.`);
          break;
        }
      }
    }

    // Try 3: If still not found, use the navSection prop itself with defaults
    if(result === undefined) {
      console.warn(
        `[QuoteBuilderForm] windowNavSection could not find match for navSection.label="${navSection.label}", ` +
        `navSection.index=${navSection.index}, user_quote_id=${window.user_quote_id}. ` +
        `Using provided navSection with default values.`
      );

      // Track this as a potential bug for monitoring
      trackEvent('quote_builder_nav_section_not_found', {
        navSection_label: navSection.label,
        navSection_index: navSection.index,
        user_quote_id: window.user_quote_id,
        window_nav_sections_count: window.nav_sections.length,
        window_nav_sections_labels: window.nav_sections.map(ns => ns.label).join(','),
        window_nav_sections_indexes: window.nav_sections.map(ns => ns.index || ns.sort_order).join(',')
      });

      // Return navSection prop with safe defaults
      result = { ...navSection };
    }

    // Ensure required fields have defaults (single source of truth: navSectionDefaults, part-type aware).
    return applyNavSectionDefaults(result);
  }

  const partType = () => {
    return navSection.part_type;
  }

  const d = (varName) => {
    if(config.dynamic_variables == undefined) { return; }

    for(let i = 0; i < config.dynamic_variables.length; i++ ){
      if(config.dynamic_variables[i].name == varName) {
        return eval(config.dynamic_variables[i].value)
      };
    }
  }

  const userUnitPriceAttributes = useRef([]);

  const addToUsetUnitPriceAttributes = (name) => {
    if (userUnitPriceAttributes.current == undefined) { userUnitPriceAttributes.current = []; }
    userUnitPriceAttributes.current.push(name);
    userUnitPriceAttributes.current = userUnitPriceAttributes.current.filter(onlyUnique);
  }

  const useLayout = () => {
    if(options == undefined) { return }
    if(config.key == undefined) { return }
    if(options.layout == undefined) {
      switch (config.key) {
        // The material section bills the STOCK sheet the finder solved on (layout_material), not the printing
        // sheet it is folded into -- a larger stock sheet holds proportionally more, so proportionally fewer are
        // needed at the same paper weight. Falls back to the printing sheet when no material sheet was solved for
        // (the plain v1 quote builder, and without-folding solutions whose stock sheet is the unconstrained dummy).
        case "material":
          options.layout = options.layout_material || options.layout_printing;
          break;

        case "hot_stamping":
        case "printing":
        case "cellophane":
        case "lacquer_coating":
        case "glueing":
        case "lamination":
        case "lamination":
        case "coating_labor":
        case "v_cut_labor":
        case "transport":
        case "profit_margin":
          options.layout = options.layout_printing;
          break;

       case "guillotine_cutting":
       case "cutting":
       case "flexo_printing_and_die_cutting":
          options.layout = options.layout_diecutting;
        break;

        default:
          console.log(`Sorry, we dont know what to do for ${config.key}.`);
      }
    }
  }

  const getInitialValueFromCache = (name) => {
    if(config == undefined || columnTitle == undefined || navSection == undefined) { return undefined; }
    const cache = cacheState.user_quote_section_properties_cached;
    if(cache == undefined || cache[navSection.label] == undefined) { return undefined; }
    let searchKey = t(config.title) + "_" + columnTitle;
    if (cache[navSection.label][searchKey] != undefined) {
      return cache[navSection.label][searchKey][name];
    }

    return undefined;
  }

  const initialValues = () => {
    useLayout();

    let values = {
      user_quote_id: window.user_quote_id,
      nav_section_column_type: navSectionColumnType,
      section_uuid: section_uuid,
      id: '',
      price_attributes: [],
      user_exchange_rates: {},
    };

    if(window.user_quote != undefined && window.user_quote.layout_part_group_windows.length > 0) {
      for(let i=0; i < window.user_quote.layout_part_group_windows.length; i++) {
       if(window.user_quote.layout_part_group_windows[i].value != undefined) {
         values[window.user_quote.layout_part_group_windows[i].attribute_name] = window.user_quote.layout_part_group_windows[i].value;
       } else {
         values[window.user_quote.layout_part_group_windows[i].attribute_name] = 'not-set';
       }
      }
    }

    if(navSection != undefined) {
      values.nav_section = navSection;
      values.row_index = rowIndex;
      values.item = item;
    }

    config.attributes.map( (config) => {
      if(config.form_type == 'price') {
        values[ 'price_attributes' ] = values[ 'price_attributes' ].concat([config.name]);
      }

      // A field the chosen manufacturing line seeds (Frank own-line pricing, `_line_seed` in the payload): the
      // CHOSEN line's value OUTRANKS a value cached from an earlier pick, so the section opens priced on the
      // line just chosen rather than a stale one. Undefined for any other field / flow -> falls through unchanged.
      const lineSeed = options && options._line_seed ? options._line_seed[config.name] : undefined;

      if(lineSeed != undefined) {
        values[config.name] = lineSeed;

      } else if(getInitialValueFromCache(config.name) != undefined) {
        values[config.name] = getInitialValueFromCache(config.name);

      } else {
        if (config.default_value != undefined) {
          values[config.name] = config.default_value;
        } else {
          if (config.default_value_eval != undefined) {
            values[config.name] = eval(config.default_value_eval);
          } else {
            if(config.default_value_in_values != undefined) {
              values[config.name] = values[config.default_value_in_values];
            } else {
              values[config.name] = defaultValueByFormType(config);
            }
          }
        }
      }
    });

    // A brand-new price has no saved record to read wastage_unit from, so seed the section's fixed unit
    // (server-stamped from the price key). The wastage card labels its count in it ("Wastage (sheets)");
    // edit mode overrides this with the record's own wastage_unit once the record loads.
    if (config.wastage_unit != undefined && values['wastage_unit'] == undefined) {
      values['wastage_unit'] = config.wastage_unit;
    }

    return values;
  }

  const [componentShowingState, setComponentShowingState] = useState(showingState == undefined ? (config.list.enabled ? STATE_LIST : STATE_NEW) : showingState);
  const [fetchInProgress, setFetchInProgress] = useState(false);
  const [childSubFormOpen, setChildSubFormOpen] = useState(false);
  const [itemIdBeingEdited, setItemIdBeingEdited] = useState(editStateId);
  // Which record's saved values are currently seeded into the form. Compared against the record being
  // edited (see editDataLoading below) to show a spinner while an edit form's data is still loading,
  // instead of flashing empty/default (invalid) values. Mirrors the legacy quote_builder tree.
  const [seededEditId, setSeededEditId] = useState(null);
  const [postOnChangeResponse, setPostOnChangeResponse] = useState({});
  const [resultsRefreshing, setResultsRefreshing] = useState(false);
  const resultsRefreshRestoreTimer = useRef();
  const [hidden, setHidden] = useState(false);
  const [values, setValues] = useState(initialValues());
  // Current values readable from a listener registered once at mount (the section-reset impact query below);
  // reading `values` there would bind to the mount-time object.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const [inCompleteMessage, setInCompleteMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  // A failed save shows its errors -- field-level messages and/or the server-error banner -- at the bottom of
  // the form, below the fold on the tall /mcc forms, and the setup wizard submits from its own "Next" outside
  // the form, so the browser never scrolls to them. Bring the first error into view after each failed attempt.
  // An always-incrementing tick (not the errorMessage value) drives the scroll, so a repeated identical error
  // still re-scrolls. Mirrors the sibling components/quote_builder/QuoteBuilderForm.
  const formRootRef = useRef(null);
  const [errorScrollTick, setErrorScrollTick] = useState(0);
  const outputCurrencies = useRef([]);
  const {register, handleSubmit, clearErrors, trigger, formState: { errors: allErrors }} = useForm();

  useEffect(() => {
    if (errorScrollTick === 0) { return; }
    const firstError = formRootRef.current && formRootRef.current.querySelector('.input-error, .alert-danger');
    if (firstError) { firstError.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  }, [errorScrollTick]);

  // Track which fields the user has interacted with, and whether a save has been attempted, so
  // that validation errors are only DISPLAYED on fields the user has reached. Validation itself
  // still runs over all fields (save is blocked by trigger()); this only gates what is shown.
  const [touchedFields, setTouchedFields] = useState({});
  const [saveAttempted, setSaveAttempted] = useState(false);

  const scrollTop = () => {
    if(window.QuoteBuilderFormScrollTop) {
      $("html, body").animate({ scrollTop: 100 }, "slow");
    }
  }

  const setValue = (id, value) => {
    setValues( (previous) => {
      if (typeof value === 'function') {
        previous[id] = value(previous);
      } else {
        previous[id] = value;
      }
      return { ...previous };
    })
  }

  // Record a field as touched. Driven by the form-level onBlur, which bubbles every child input's
  // blur (event.target.name is the blurred field). Keeps premature errors off untouched fields,
  // e.g. picking a colour count must not flag the still-untouched price field further down.
  const markFieldTouched = (name) => {
    if (!name) { return; }
    setTouchedFields((previous) => previous[name] ? previous : { ...previous, [name]: true });
  };

  // Only the errors the user should currently see: a field's error shows once that field is
  // touched, or once a save has been attempted (which reveals everything at once). The single
  // errors={errors} prop below consumes this filtered set, so the gating applies form-wide.
  const errors = {};
  for (const name in allErrors) {
    if (saveAttempted || touchedFields[name]) {
      errors[name] = allErrors[name];
    }
  }

  // Start each form context (new record, editing a different record, switching list/edit) with a
  // clean slate so previously touched fields / a prior save attempt don't carry over and reveal
  // errors on a freshly opened form.
  useEffect(() => {
    setTouchedFields({});
    setSaveAttempted(false);
  }, [componentShowingState, itemIdBeingEdited])

  const getDefaultFetchPath = () => {
    if(editedKlass == "UserUnitPriceAttribute") {
      return `/quote-builder/user_unit_price_attributes/properties/${itemIdBeingEdited}`
    }

    return (config.fetch_path || default_fetch_path).replace(':id', itemIdBeingEdited);
  }

  const getDefaultUpdatePath = () => {
    if(editedKlass == "UserUnitPriceAttribute") {
      return `/quote-builder/user_unit_price_attributes/update_properties/${itemIdBeingEdited}`
    }

    return config.submit.update_path || default_update_path;
  }

  const getFetchEnabled = () => {
    if(editedKlass == "UserUnitPriceAttribute") { return true }
    return config.fetch_enabled;
  }


  const save = async () => {
    if(disabled || readOnly) { return };
    setSubmitInProgress( () => true )

    setFetchInProgress(true);

    if(beforeSave != undefined) beforeSave();

    let url;

    switch(componentShowingState) {
      case STATE_NEW:
        url = (config.submit.create_path || default_create_path).replace(':key', config.key)
        break;

      case STATE_COPY_FROM_SYSTEM:
        url = (config.submit.create_path || default_create_path).replace(':key', config.key)
        break;

      case STATE_EDIT:
        url = getDefaultUpdatePath()
        break;

      default:
        throw 'invalid componentShowingState';
    }

    if(await trigger()) {

      applySaveValueInto(config.attributes, values, makeVap);

      if(has_file.current != undefined && has_file.current != '') {
        values.file_fields = [ has_file.current ] ;
        values[ has_file.current ] = $(`[name='${has_file.current }']`)[0].files[0]
      }

      QueuedPostRequest({
        url: url,
        data: values,
        callback: (data) => {
          setItemIdBeingEdited(null);
          setFetchInProgress(false);
          document.dispatchEvent(new CustomEvent('quote_builder.user_unit_price_saved', {}) );
          if(afterSave != undefined) {
            afterSave(data.id);
          } else {
            setComponentShowingState(STATE_LIST);
          }

         setSubmitInProgress( () => false )
        },
        400: (data) => {
          setErrorMessage(data.errors);
          setErrorScrollTick((tick) => tick + 1);
          setItemIdBeingEdited(null);
          setFetchInProgress(false);
          setSubmitInProgress( () => false );
        }
      }).catch((error) => {
        console.error('[QuoteBuilderForm save] Unhandled error:', error);
        setFetchInProgress(false);
        setSubmitInProgress(() => false);
        setItemIdBeingEdited(null);
      })
    } else {
      // Client-side validation failed, so nothing is saved and no banner appears; bring the first field error
      // into view like the server-error path above.
      setSubmitInProgress( () => false );
      setErrorScrollTick((tick) => tick + 1);
    }
  }

  const cancel = () => {
    clearErrors();
    setItemIdBeingEdited(null);
    setComponentShowingState(STATE_LIST);
    if(afterCancel != undefined) {
      afterCancel();
    }
  }

  const windowValue = () => {
    let key = 'window_type__default';

    if(item && item.layout_part_group_code != '' && item.layout_part_group_code != undefined && item.layout_part_group_code != null) {
      key = `window_type_${item.layout_part_group_code}`;
    }

    return values[key];
  }

  // requestGetExtraItems
  const requestGetExtraItemsRef = useRef([]);
  const requestGetExtraItems = (key_value) => {
    requestGetExtraItemsRef.current.push([key_value, section_uuid]);
  }

  const processGetExtraItemRequests = () => {
    for(let i = 0; i < requestGetExtraItemsRef.current.length; i++) {
      let args = requestGetExtraItemsRef.current[i];
      getExtraItems(args[0], args[1], values, item, options);
    }

    requestGetExtraItemsRef.current = [];
    if(config.get_extra_items_on_change) {
      getExtraItems({ key: config.get_extra_items_key, value: config.get_extra_items_value }, eval(config.get_extra_items_uuid_eval) , values, item, options);
    }
  }

  //requestPublishItemOnChange
  const requestPublishItemOnChangeRef = useRef([]);
  const requestPublishItemOnChange = (msg) => {
    requestPublishItemOnChangeRef.current.push(msg);
  }

  const processPublishItemOnChange = () => {
    for(let i=0; i < requestPublishItemOnChangeRef.current.length; i++) {
      let message = requestPublishItemOnChangeRef.current[i];
      let publishItemKey = message.publish_item_key != undefined ? message.publish_item_key : USER_UNIT_PRICE_CHANGED_WITH_ITEM_EVENT_KEY;

      // Stamp the CURRENT values, not the render-scoped `values`: this flush runs from a post-on-change callback /
      // setTimeout captured in an earlier render, so `values` is stale. A field that settles its value after an
      // async price-list fetch (e.g. the die-cutting machine restored from not-available to the real machine) would
      // otherwise publish the pre-settle value, leaving listeners like the cutting-board readonly gate stuck on it.
      message.values = valuesRef.current;
      message.item = item;
      message.nav_section = navSection;

      // console.log({key: publishItemKey, message: message})
      document.dispatchEvent(new CustomEvent(publishItemKey, { detail: message } ));
    }
    requestPublishItemOnChangeRef.current = [];
  }

  const formFieldFactory = (config, index) => {
    return <FormFieldFactory
      config={config}
      libraryTabConfig={attributeTabConfigFor(config.key)}
      index={index}
      values={values}
      options={options}
      setValue={setValue}
      readOnlyValues={readOnlyValues}
      setFetchInProgress={setFetchInProgress}
      setChildSubFormOpen={setChildSubFormOpen}
      disabled={disabled}
      readOnly={readOnly}
      register={register}
      errors={errors}
      context={context}
      item={item}
      navSection={navSection}
      requestPublishItemOnChange={requestPublishItemOnChange}
      requestGetExtraItems={requestGetExtraItems}
      navSectionColumnType={navSectionColumnType}
      windowValue={windowValue()}
      addToUsetUnitPriceAttributes={addToUsetUnitPriceAttributes}
      has_file={has_file}
      showLongDescriptions={showLongDescriptions}
      d={d}
      v={v}
      va={va}
      VALUE_NOT_AVAILABLE={VALUE_NOT_AVAILABLE}
      VALUE_NEW={VALUE_NEW}
      is_not_new_value={is_not_new_value}
      is_not_new_value_va={is_not_new_value_va}
      vap={vap}
      onAttributesLoaded={registerAttributeProperties}
      dataSourcesEnabled={window.quote_builder_data_sources_enabled}
      itemIdBeingEdited={itemIdBeingEdited}
    />;
   }

   // Render the attribute list, coalescing EVERY consecutive run of grouped fields (the pricing tiers:
   // count / price / wastage) into ONE table row -- one PricingThresholdCard per group run, all in a single
   // cell -- so the tier cards share one zebra stripe and read as a single unit (mirrors the settings-tree
   // form; this form drives the inline machine create/edit popup). Each card is still gated on its own
   // group's first-field visible_if; the row is skipped when every card in it is hidden. Ungrouped fields
   // render exactly as before.
   const renderAttributes = (attributes) => {
     const rendered = [];

     for(let index = 0; index < attributes.length; index++) {
       const attribute = attributes[index];

       // A `grouped_form` island: consecutive fields sharing a `grouped_form` key render inside ONE bordered
       // card, titled by the first field's `grouped_form_title` (issue #5450 -- the Lak section's Front / Back
       // blocks). Each field keeps its own visible_if (rendered normally inside the card); the whole card is
       // skipped when every field in it is hidden, so an inactive face draws nothing.
       if(attribute.grouped_form != undefined) {
         const groupedFormKey = attribute.grouped_form;
         const groupedFormStartIndex = index;
         const groupedFormFields = [];
         while(index < attributes.length && attributes[index].grouped_form === groupedFormKey) {
           groupedFormFields.push(attributes[index]);
           index++;
         }
         index--;

         // A hidden field (a mirror, updated_at) never draws anything, so it must not keep the card alive: the
         // card renders only when a NON-hidden field is actually visible.
         const anyVisible = groupedFormFields.some((field) => field.form_type !== 'hidden' && (field.visible_if == undefined || eval(field.visible_if)));
         if(anyVisible) {
           rendered.push(
             <tr key={`grouped_form_${groupedFormKey}_${groupedFormStartIndex}`}>
               <td colSpan={2} style={{ padding: '10px 12px' }}>
                 <div style={{ border: '1px solid #e3e3e6', borderRadius: '7px', overflow: 'hidden', background: '#fbfcfd' }}>
                   <div style={{ background: '#f9f9f9', padding: '9px 14px', fontWeight: 600, fontSize: '13px', color: '#4a4a52', borderBottom: '1px solid #e3e3e6' }}>
                     {t(groupedFormFields[0].grouped_form_title)}
                   </div>
                   <table className="table quote_builder_table" style={{ margin: 0 }}><tbody>
                     {groupedFormFields.map((field, offset) => (
                       <React.Fragment key={field.name || offset}>{formFieldFactory(field, groupedFormStartIndex + offset)}</React.Fragment>
                     ))}
                   </tbody></table>
                 </div>
               </td>
             </tr>
           );
         }
         continue;
       }

       if(attribute.group == undefined) {
         rendered.push(<React.Fragment key={attribute.name || index}>{formFieldFactory(attribute, index)}</React.Fragment>);
         continue;
       }

       const regionStartIndex = index;
       const cards = [];

       while(index < attributes.length && attributes[index].group != undefined) {
         const groupKey = attributes[index].group;
         const groupStartIndex = index;
         const groupFields = [];
         while(index < attributes.length && attributes[index].group === groupKey) {
           groupFields.push(attributes[index]);
           index++;
         }

         // Group metadata (title/icon/layout) sits on the first field, and by default the card renders the
         // whole run gated on that first field's visible_if. A group can opt into per-field visibility with
         // group_filter_by_visible_if (the thickness card: unit-select variants plus length-or-gsm min/max,
         // only one of each visible at a time) -- then each field is filtered by its OWN visible_if, the
         // metadata comes from the first VISIBLE field (the run's first field, a unit-specific select, may
         // itself be hidden), and the card renders iff at least one field is visible.
         const firstField = groupFields[0];
         const filterByVisibility = groupFields.some((f) => f.group_filter_by_visible_if);
         const cardFields = filterByVisibility
           ? groupFields.filter((f) => f.visible_if == undefined || eval(f.visible_if))
           : groupFields;
         const metadataField = filterByVisibility ? (cardFields[0] || firstField) : firstField;
         const cardVisible = filterByVisibility
           ? cardFields.length > 0
           : (firstField.visible_if == undefined || eval(firstField.visible_if));

         if(cardVisible) {
           cards.push(
             <PricingThresholdCard
               key={`group_${groupKey}_${groupStartIndex}`}
               groupTitle={metadataField.group_title}
               groupDescription={metadataField.group_description}
               groupIcon={metadataField.group_icon}
               groupLayout={metadataField.group_layout}
               fields={cardFields}
               values={values}
               setValue={setValue}
               register={register}
               errors={errors}
               disabled={disabled}
               currency={values['currency']}
               dataSourcesEnabled={window.quote_builder_data_sources_enabled}
               userUnitPriceId={itemIdBeingEdited}
             />
           );
         }
       }
       index--;

       // One row for the whole tier group so the cards share a stripe; skip it when every card is hidden.
       if(cards.length > 0) {
         rendered.push(
           <tr key={`group_row_${regionStartIndex}`}>
             <td colSpan={2} className="pricing-threshold-card-cell">
               {cards}
             </td>
           </tr>
         );
       }
     }

     return rendered;
   }

   const myFormRef = useRef();

   const submit = async () => {
    // Reveal every field's error on a save attempt (this is the real Save path: the button is
    // type='button' with onClick={submit}, so an invalid save would otherwise show nothing).
    setSaveAttempted(true);
    if(await trigger()) {
      myFormRef.current.handleSubmit(save);
    } else {
      // Validation failed, so no save is attempted; bring the first field error into view (the setup wizard's
      // Next submits from outside the form, so nothing scrolls to it).
      setErrorScrollTick((tick) => tick + 1);
    }
  };


  const onPostOnChangeCompletedEnabled = useRef(false);

  const onPostOnChangeCompleted = (data) => {
    document.dispatchEvent(new CustomEvent('quote_builder.section_result_updated', { detail: section_uuid }));

    if(data.user_quote_section_properties_cached != undefined) {
      // Use cache context dispatch instead of direct window mutation
      cacheDispatch({ 
        type: 'UPDATE_CACHE', 
        payload: data.user_quote_section_properties_cached 
      });
    }

    if(data.user_quote != undefined) {
      window.user_quote = data.user_quote;
    }

    if(data.nav_sections != undefined) {
      window.nav_sections = data.nav_sections;
    }
  }

  const anyUserUnitPriceAttributeIsSet = useRef(false);
  const valuesHash = useRef();

  // The parent (QuoteBuilder.jsx gatedUntilMaterialPriced) greys this section out via the `disabled` prop while the
  // column's Material section is not yet priced, handing down the material_section_incomplete reason. That gate
  // leaves the section's own UserUnitPrice pickers holding their selection, so a section that was already priced
  // keeps billing under the grey (its price stays in UserQuote#total). With no material priced NOTHING in the
  // column should bill, so EVERY section clears its pickers and posts the clear here, regardless of
  // reset_user_unit_prices_when_disabled -- that opt-in flag governs only the disabled_if path below. Every OTHER
  // disabled state (a false render condition, which carries no reason) stays fully blocked, exactly as before.
  const gatedByMaterialIncomplete = disabled && disabledReason === 'quote_builder.material_section_incomplete';
  const clearPricesWhileGated = gatedByMaterialIncomplete;

  // The material gate is ALSO transiently on during a normal reload of an already-priced quote -- the window
  // before the column's Material section re-posts and re-reports its saved price (materialResultAvailable starts
  // false). The clear-while-gated effect below must NOT fire in that window: it would zero this section's saved
  // pickers and persist the zero. A machine field recovers (the chosen line re-seeds it via _line_seed), but a
  // cutting board / sorting board / metal strip has no re-seed, so its saved selection would be destroyed on every
  // reload. So treat the material gate as a real withdrawal only once it has actually LIFTED at least once this
  // session (the Material section reported a priced result, then it was later removed). A genuinely-unpriced column
  // never lifts, and its dependent pickers are already not-available there, so nothing is lost by not clearing.
  const materialGateHasLifted = useRef(false);
  useEffect(() => {
    if(!clearPricesWhileGated) { materialGateHasLifted.current = true; }
  }, [clearPricesWhileGated]);

  const delay = 200;
  let debounceTimer;
  const postOnChange = async () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      postOnChangeAction();
    }, delay);
  };

  // Typed values commit when typing pauses, and on Enter, instead of waiting for focus to leave the
  // field. Straight to postOnChangeAction rather than through postOnChange, so the pause here is the
  // whole delay rather than this one plus that one's.
  const { noteUserChange, commitNow } = useCommitOnPause({
    values: values,
    enabled: config.post_on_change,
    commit: () => postOnChangeAction(),
  });

  const postOnChangeActionInProgress = useRef(false);
  const postOnChangeAction = async () => {

    // clearPricesWhileGated lets a reset-when-disabled section still POST its (now cleared) pickers while the
    // material gate greys it, so its price leaves the total; every other disabled/hidden state blocks the post.
    if((disabled && !clearPricesWhileGated) || hidden || postOnChangeActionInProgress.current || fetchInProgress) { return };
    if(config.post_on_change_needs_user_quote_id && window.user_quote_id == undefined) { return };
    if(valuesHash.current != undefined && hash(values) == valuesHash.current) { return }

    postOnChangeActionInProgress.current = true;
    setFetchInProgress( () => true );

    setInCompleteMessage(null);
    setErrorMessage(null);
    // Deliberately do NOT blank postOnChangeResponse here: keeping the last results mounted lets a refetch
    // update the numbers in place instead of unmounting the whole table and popping it back (the visible
    // "blink"). They are replaced when the response lands, cleared on error, or cleared in the else branch
    // below when nothing gets sent.

    if(anyUserUnitPriceAttributeIsSet.current == false && userUnitPriceAttributes.current != undefined) {
      for(let i = 0; i < userUnitPriceAttributes.current.length; i++) {
        if(values[userUnitPriceAttributes.current[i]] != VALUE_NOT_AVAILABLE) {
          anyUserUnitPriceAttributeIsSet.current = true;
        }
      }
    }

    if(anyUserUnitPriceAttributeIsSet.current == false && userUnitPriceAttributes.current != undefined && userUnitPriceAttributes.current.length > 0) {
      setTimeout(processPublishItemOnChange, 250);
      setFetchInProgress(() => false );
      postOnChangeActionInProgress.current = false;
      if(window.user_quote_finalised){ setHidden( () => true) }
      return;
    }

    if(await trigger()) {
      let valuesToPost = { ...values };
      valuesHash.current = hash(values);
      if(has_file.current != undefined && has_file.current != '') {
        // todo
      }

      QueuedPostRequest({
        url: config.post_on_change_path,
        data: valuesToPost,
        callback: (data) => {

          if(data.results != undefined) {
            setPostOnChangeResponse(data.results);
            outputCurrencies.current = data.results.currencies;
            if(window.user_quote_finalised && data.results.total_price_in_pence == 0){
              setHidden( () => true)
            }

            if(data.results.total_price_in_pence > 0){
              emitEvent("total_price_in_pence_set", {
                label: navSection.label,
                total_price_in_pence: data.results.total_price_in_pence
              } )
            }
          }

          pushNavSectionAutoWastages(data, emitEvent);

          onPostOnChangeCompleted(data);
          onPostOnChangeCompletedEnabled.current = true;
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(() => false );
          postOnChangeActionInProgress.current = false;
        },
        422: (data) => {
          setInCompleteMessage(data.message);
          setPostOnChangeResponse({line_item_prices: []});
          pushNavSectionAutoWastages(data, emitEvent);
          if(onPostOnChangeCompletedEnabled.current) { onPostOnChangeCompleted(data); }
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(() => false );
          postOnChangeActionInProgress.current = false;
          if(window.user_quote_finalised){ setHidden( () => true) }
        },
        400: (data) => {
          setErrorMessage(data.message);
          setPostOnChangeResponse({line_item_prices: []});
          pushNavSectionAutoWastages(data, emitEvent);
          if(onPostOnChangeCompletedEnabled.current) { onPostOnChangeCompleted(data); }
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(() => false );
          postOnChangeActionInProgress.current = false;
        }
      }).catch((error) => {
        console.error('[QuoteBuilderForm postOnChange] Unhandled error:', error);
        setFetchInProgress(() => false);
        postOnChangeActionInProgress.current = false;
        setPostOnChangeResponse({line_item_prices: []});
      });
    } else {
      // Nothing was sent, so nothing is in flight. Without this the section stays wearing
      // `disabledForm` -- greyed out, and blocked from ever committing again by the in-progress guard
      // at the top of this method -- for as long as the page is open. It is easy to land on while
      // editing: a field emptied on the way to a new value fails its NonEmptyValidator, and the
      // commit that follows a pause in typing arrives right in that gap.
      setPostOnChangeResponse([]);
      setFetchInProgress(() => false);
      postOnChangeActionInProgress.current = false;
    }
  }

  const validateOnChange = () => {
    if(disabled) { return };
    setInCompleteMessage(null);
    setErrorMessage(null);

    trigger();
  }


  const disabledIf = () => {
    if(submitInProgress) { return true }
    if(childSubFormOpen) { return true }
    return (config.submit.disabled_if != undefined && eval(config.submit.disabled_if))
  }

  const sectionClearEnabled = useRef(false);

  const onKeyUpWrapped = (e) => {
    // sectionClearEnabled.current = true;
    postOnChange()
  }

  const onBlurWrapped = (e) => {
    // sectionClearEnabled.current = true;
    postOnChange()
  }
  const cookieNameforLongDescription = 'quote_builder_settings_show_long_descriptions';
  const [showLongDescriptions, setShowLongDescriptions] = useState(true);

  const handleToggleLongDescriptions = () => {
    setShowLongDescriptions(!showLongDescriptions);
    setCookie(cookieNameforLongDescription, !showLongDescriptions, 365)
  }


  const [localDisabled, setLocalDisabled] = useState(false);
  const [localDisabledReason, setLocalDisabledReason] = useState('');

  const [isSystem, setIsSystem] = useState(false);
  const handleIsSystemChange = (e) => {
    QueuedPostRequest({
      url: `/quote-builder/user_unit_prices/update-is-system`,
      data: {
        id: itemIdBeingEdited,
        is_system: e.target.checked
      },
      callback: (data) => {
        setIsSystem(() => {return data.is_system})
      }
    }).catch((error) => {
      console.error('[QuoteBuilderForm handleIsSystemChange] Unhandled error:', error);
    })
  }

  const shouldFetchForEditMode = () => {
    return getFetchEnabled() && itemIdBeingEdited > 0 && componentShowingState == STATE_EDIT;
  }

  const shouldFetchForCopyFromSystemMode = () => {
    return config.fetch_enabled && itemIdBeingEdited > 0 && componentShowingState == STATE_COPY_FROM_SYSTEM;
  }

  const fetchDataForEditMode = () => {
    const idBeingFetched = itemIdBeingEdited;
    setFetchInProgress(true);

    GetRequest({
      url: getDefaultFetchPath(),
      callback: (data) => {
        data.price_attributes = initialValues()['price_attributes'];
        setFetchInProgress(true);

        for (const [key, value] of Object.entries(data)) {
          // A price that arrives null (a machine-only record saved before pricing existed)
          // must not lock the input in its "Loading..." state: seed an editable zero instead.
          if (value === null && data.price_attributes.includes(key)) {
            setValue(key, zeroPriceValueFor(key));
          } else if (value === null && multiSelectDefaultFor(key) != undefined) {
            setValue(key, multiSelectDefaultFor(key));
          } else if (value === null && configuredDefaultFor(key) != undefined) {
            setValue(key, configuredDefaultFor(key));
          } else {
            setValue(key, value);
          }
        }

        seedSaveValueIntoFields(config.attributes, data, setValue, makeVap);

        if(data.is_system != undefined && setIsSystem != undefined) {
          setIsSystem(data.is_system);
        }

        scrollTop();
        setFetchInProgress(false);
        setSeededEditId(idBeingFetched);
      },
      // On any failure mark this record resolved so the form shows rather than spinning forever.
      onError: () => {
        setFetchInProgress(false);
        setSeededEditId(idBeingFetched);
      }
    })
  }

  const fetchDataForCopyFromSystemMode = () => {
    setFetchInProgress(true);

    GetRequest({
      url: default_fetch_system_path.replace(':id', itemIdBeingEdited),
      callback: (data) => {
        data.price_attributes = initialValues()['price_attributes'];
        setFetchInProgress(true);

        for (const [key, value] of Object.entries(data)) {
          if(config.mark_system.system_attribute_names.includes(key)) {
            // Same null handling as the edit-mode fetch above: a null pushed into a controlled
            // input is ignored by React and leaves the previously displayed value, so seed the
            // field's zero/default instead of raw null.
            if (value === null && data.price_attributes.includes(key)) {
              setValue(key, zeroPriceValueFor(key));
            } else if (value === null && multiSelectDefaultFor(key) != undefined) {
              setValue(key, multiSelectDefaultFor(key));
            } else if (value === null && configuredDefaultFor(key) != undefined) {
              setValue(key, configuredDefaultFor(key));
            } else {
              setValue(key, value);
            }
          }
        }

        setValue('copied_from_id', itemIdBeingEdited);

        scrollTop();
        setFetchInProgress(false);

        setTimeout(() => {
          setComponentShowingState(() => { return STATE_LIST});
          setTimeout(() => {setComponentShowingState(() => { return STATE_COPY_FROM_SYSTEM});}, 2)
        }, 2)
      }
    })
  }

  // useEffect starts............................................................................
  useEffect(() => {
    if(disabled || readOnly) { return };

    if(shouldFetchForEditMode()) {
      fetchDataForEditMode();
      return;
    }

    if(shouldFetchForCopyFromSystemMode()) {
      fetchDataForCopyFromSystemMode();
      return;
    }

  }, [ itemIdBeingEdited ]);

  // useEffect( () => {
  //   if(disabled || readOnly) { return };

  //   if(componentShowingState == STATE_NEW) {
  //     if(window.QuoteBuilderFormScrollTop) { scrollTop() }
  //     setValues( { ...initialValues() } );
  //   }
  // }, [componentShowingState])

  useEffect(() => {
    const keyDownHandler = event => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  useEffect(() => {
   if(disabled && !clearPricesWhileGated) { return };

   if(config.post_on_change && values['updated_at'] != undefined){
    postOnChange();
   }
  }, [values['updated_at']]);

  useEffect(() => {
    let event = new CustomEvent('quote_builder.output_currencies_changed', { "detail": outputCurrencies.current });
    document.dispatchEvent(event);

  }, [outputCurrencies.current]);

  useEffect(() => {
    if(config.post_on_change) {
      let listener = function(e) {
        setValue('user_exchange_rates', (previous) => {
          previous['user_exchange_rates'][e.detail.key] = e.detail.value;
          return { ...previous['user_exchange_rates'] };
        });

        setValue('updated_at', Date.now());
      };

      document.addEventListener('quote_builder.currency_exchange_rate_changed', listener);
      return () => { document.removeEventListener('quote_builder.currency_exchange_rate_changed', listener);}
    }
  }, []);

  if(config.disabled_if != undefined) {
    useEffect(() => {
      // disabled_if is either a single expression (paired with disabled_if_reason) or a list of { if, reason }
      // rules evaluated in order -- the first whose condition holds disables the section and supplies its reason.
      // The list form lets one section carry more than one disable reason (e.g. lacquer_coating gates on both the
      // İç sıvama printing question and the selected material's lacquer capability, each with its own message).
      const disabledRules = Array.isArray(config.disabled_if)
        ? config.disabled_if
        : [{ if: config.disabled_if, reason: config.disabled_if_reason }];
      const matchedRule = disabledRules.find((rule) => eval(rule.if));
      if( matchedRule ) {
        setLocalDisabled( (_prev) => true )
        setLocalDisabledReason( (_prev) => t(matchedRule.reason) );
      } else {
        setLocalDisabled( (_prev) => false )
        setLocalDisabledReason( (_prev) => null);
      }
    }, [values])
  }

  // The section's top-level UserUnitPrice field names (config.attributes is stable per mount). The material gate
  // clears these for EVERY gated section, and the disabled_if path for opted-in ones. Every current picker is
  // top-level; a section that ever nests a UserUnitPrice picker would need this widened to the rendered set
  // (userUnitPriceAttributes), or the gate would silently fail to clear it and it would keep billing under the grey.
  const userUnitPriceFieldNames = config.attributes
    .filter((attribute) => attribute.form_type == 'UserUnitPrice')
    .map((attribute) => attribute.name);

  // A section that has become not-applicable greys out but the UserUnitPrice pickers stay mounted, so a
  // previously selected machine keeps showing (and its saved summary) under the grey. This happens two ways:
  // disabled_if makes it locally not-applicable (localDisabled), and the parent's material gate greys it while
  // the column's Material section is unpriced (clearPricesWhileGated). Clear every UserUnitPrice field to
  // not-available so the picker matches the section's state, and bump updated_at so post-on-change persists the
  // cleared selection rather than leaving the stale one in the total (and to reappear on reload). The material
  // gate clears every section; the disabled_if path clears only sections that opt in with
  // reset_user_unit_prices_when_disabled.
  //
  // Keyed on the fields' current values (not just the disabled flags), so it re-fires and re-clears if a value
  // reappears while the section stays disabled -- notably UserUnitPrice's own stash, which re-applies the
  // saved id once its list finishes loading on a fresh page load (form_fields/UserUnitPrice.jsx). Converges:
  // once every field is not-available the signature is stable and nothing is left to clear.
  useEffect(() => {
    if(!((clearPricesWhileGated && materialGateHasLifted.current) || (localDisabled && config.reset_user_unit_prices_when_disabled))) { return; }

    let anyReset = false;
    userUnitPriceFieldNames.forEach((name) => {
      if(values[name] != VALUE_NOT_AVAILABLE) {
        setValue(name, VALUE_NOT_AVAILABLE);
        anyReset = true;
      }
    });

    if(anyReset) {
      // A selection WAS set (we are clearing it), so the section's write-once "any price set" latch must be
      // true for postOnChangeAction to POST the clear instead of taking its no-price early-return -- otherwise
      // a fresh load (latch still false) blanks the display but never persists it server-side.
      anyUserUnitPriceAttributeIsSet.current = true;
      setValue('updated_at', Date.now());
    }
  }, [localDisabled, clearPricesWhileGated, userUnitPriceFieldNames.map((name) => values[name]).join('|')])

  // Run the section's disabled_if rules against a candidate values object and return the first matched rule (or
  // undefined). `values` is shadowed so the rule expressions -- which read values.* -- see the candidate, while
  // options / d(...) still resolve from the component scope, exactly as the disabled_if effect above evaluates them.
  // A throwing rule is treated as not-matched (not-disabled): the same fail-open the real disabled_if effect would
  // hit, so the query and the actual reset agree -- neither disables the section, so neither confirm nor reset fires.
  const matchedDisabledRuleFor = (candidateValues) => {
    const values = candidateValues; // eslint-disable-line no-unused-vars -- read by eval(rule.if)
    const disabledRules = Array.isArray(config.disabled_if)
      ? config.disabled_if
      : [{ if: config.disabled_if, reason: config.disabled_if_reason }];
    return disabledRules.find((rule) => { try { return eval(rule.if); } catch(evaluationError) { return false; } });
  };

  // Whether this section takes part in the pre-commit "will this material change reset something?" handshake: it
  // either MIRRORS the changed material (so it can publish the prospective mirror value the pickers need to test
  // their own filters against) or it zeroes its own pickers when a material capability disables it.
  const sectionMirrorsAMaterial = config.attributes.some((attribute) =>
    (attribute.listen_conditions || []).some((condition) => condition.type == 'fetch_changed_user_price_and_set_value')
  );

  // Answer the material picker's synchronous impact query, fired BEFORE the change commits
  // (form_fields/UserUnitPrice.jsx). Two contributions:
  //  1. Publish this section's PROSPECTIVE mirror values (material_printing_types, material_hot_stamped_enabled, ...)
  //     by re-running its own fetch_changed_user_price_and_set_value listeners against the changed material row --
  //     pickers do not hold the mirror wiring, so this is how they learn what their filter input would become.
  //  2. Report itself when it zeroes its pickers on disable (reset_user_unit_prices_when_disabled), is currently
  //     priced, and the prospective material would NEWLY disable it -- the exact disabled_if decides, no hardcoded
  //     capability->section map, no extra fetch (the capability fields ride on the row already).
  useEffect(() => {
    if(!sectionMirrorsAMaterial && !(config.reset_user_unit_prices_when_disabled && config.disabled_if != undefined)) { return; }

    const onImpactQuery = (event) => {
      const detail = event.detail || {};
      if(detail.changedUserUnitPrice == undefined || detail.publishItemKey == undefined) { return; }
      // Scope as the material mirror does (sections.yml `if: sameItem()`): same nav section, same item uuid, AND
      // same is_item_extra / extra_item_reason -- an extra-item column reuses the base item's uuid, so those two
      // are what tell a base column's change apart from an extra column's (else a base change would wrongly report
      // an extra column's section).
      if(navSection == undefined || navSection.index !== detail.navSectionIndex) { return; }
      if(item == undefined || detail.itemUuid == undefined || item.uuid !== detail.itemUuid) { return; }
      if((item.is_item_extra || false) !== (detail.isItemExtra || false)) { return; }
      if((item.extra_item_reason || null) !== (detail.extraItemReason || null)) { return; }

      const current = valuesRef.current;
      const changed_user_unit_price = detail.changedUserUnitPrice; // eslint-disable-line no-unused-vars -- read by value_eval
      const prospective = { ...current };
      let mirrorsThisMaterial = false;
      config.attributes.forEach((attribute) => {
        (attribute.listen_conditions || []).forEach((condition) => {
          if(condition.type == 'fetch_changed_user_price_and_set_value' && condition.listen == detail.publishItemKey && condition.value_eval != undefined) {
            mirrorsThisMaterial = true;
            try {
              const mirroredValue = eval(condition.value_eval);
              prospective[attribute.name] = mirroredValue;
              // Phase 1: publish it so the pickers can evaluate their filters (phase 2, UserUnitPrice.jsx).
              if(detail.prospectiveValues != undefined) { detail.prospectiveValues[attribute.name] = mirroredValue; }
            } catch(evaluationError) { /* leave current */ }
          }
        });
      });
      if(!mirrorsThisMaterial) { return; }

      // Section-disable impact: only a reset-when-disabled section, currently priced, the change NEWLY disables.
      if(!config.reset_user_unit_prices_when_disabled || config.disabled_if == undefined || !Array.isArray(detail.impacts)) { return; }
      const hasPricedPicker = userUnitPriceFieldNames.some((name) => {
        const fieldValue = current[name];
        return fieldValue != undefined && fieldValue != '' && fieldValue != VALUE_NOT_AVAILABLE;
      });
      if(!hasPricedPicker) { return; }
      if(matchedDisabledRuleFor(current) != undefined) { return; } // already disabled -> nothing more to lose
      const matchedAfter = matchedDisabledRuleFor(prospective);
      if(matchedAfter != undefined) {
        detail.impacts.push({
          section: config.title != undefined ? t(config.title) : '',
          reason: t(matchedAfter.reason),
        });
      }
    };

    // window, not document: the query is a synchronous request/response, and document listeners are stubbed under
    // jest (window ones are not), so this keeps the handshake testable.
    window.addEventListener('quote_builder.section_reset_impact_query', onImpactQuery);
    return () => { window.removeEventListener('quote_builder.section_reset_impact_query', onImpactQuery); };
  }, [])

  // The write-once "any price was set" latch must also flip for a machine that was ALREADY selected when the
  // section loaded -- seeded from the saved quote, or re-applied by UserUnitPrice once its list finishes loading
  // (form_fields/UserUnitPrice.jsx) -- not only for one picked this session. Without this, unselecting a
  // pre-selected machine before touching anything else takes postOnChangeAction's no-price early-return, so the
  // clear is never POSTed and the nav section keeps a stale automatic fire adeti. Keyed on the field values so it
  // fires when the seeded id lands; the latch is write-once, so the unselect itself never un-sets it.
  useEffect(() => {
    if(anyUserUnitPriceAttributeIsSet.current) { return; }

    const anySelected = userUnitPriceFieldNames.some((name) => {
      return values[name] != undefined && values[name] != VALUE_NOT_AVAILABLE;
    });
    if(anySelected) { anyUserUnitPriceAttributeIsSet.current = true; }
  }, [userUnitPriceFieldNames.map((name) => values[name]).join('|')])

  // Section-level hidden_if: unlike disabled_if (greyed out with a reason), a hidden section is
  // removed from view entirely while staying mounted, so its hidden fields keep listening and it
  // keeps posting on changes (letting its calculator clear its saved total server-side).
  const [localHidden, setLocalHidden] = useState(false);
  if(config.hidden_if != undefined) {
    useEffect(() => {
      if( eval(config.hidden_if) ) {
        setLocalHidden( (_prev) => true )
      } else {
        setLocalHidden( (_prev) => false )
      }
    }, [values])
  }

  // Dim the section smoothly while a price refetch runs, then restore its opacity 100ms after the last
  // fetch settles. The delay bridges back-to-back refetches (fetchInProgress dips false then true again)
  // so a burst reads as one gentle dip instead of a strobe -- and paired with the opacity transition on
  // the wrapper below, it replaces the old hard flash of `disabledForm`'s opacity: 0.5.
  useEffect(() => {
    if(fetchInProgress) {
      clearTimeout(resultsRefreshRestoreTimer.current);
      setResultsRefreshing(true);
      return;
    }
    resultsRefreshRestoreTimer.current = setTimeout(() => setResultsRefreshing(false), 100);
  }, [fetchInProgress])

  useEffect(() => () => clearTimeout(resultsRefreshRestoreTimer.current), [])

  // This section has a priced result once its calculator returns at least one line item -- the same signal
  // that renders the results table below. A coordinating parent gates a sibling on it: the material section
  // reports up through onResultAvailabilityChange so the rest of its nav column stays disabled until the
  // material is priced. Only the material section is handed the callback, so this is inert everywhere else.
  const hasPricedResult =
    postOnChangeResponse != undefined &&
    postOnChangeResponse.line_item_prices != undefined &&
    postOnChangeResponse.line_item_prices.length > 0;

  useEffect(() => {
    if(onResultAvailabilityChange == undefined) { return; }
    onResultAvailabilityChange(hasPricedResult);
  }, [hasPricedResult])
  // useEffect ends................................................................

  if( (disabled || localDisabled) && window.user_quote_finalised ) { return }
  if( hidden && window.user_quote_finalised ) { return }

  // Show a spinner (instead of the form) while an edit form's saved values are still loading, so the
  // empty/default (invalid) values never flash. Derived in render (never a post-paint frame) and
  // gated to only when a fetch will actually run. Mirrors the legacy quote_builder tree.
  const editDataLoading =
    componentShowingState == STATE_EDIT &&
    !disabled &&
    !readOnly &&
    getFetchEnabled() &&
    itemIdBeingEdited > 0 &&
    String(seededEditId) !== String(itemIdBeingEdited);

  // Narrow the frequently-used ("Sık kullanılanlar") system list to the boards the section can use (hard cardboard
  // to those within tolerance of the drawing thickness), from the one shared rule -- so this tree and the /mcc
  // picker never diverge. options.material_thickness_in_mm is the dieline's malzeme in mm (0/absent -> unfiltered).
  // (This tree does not yet pass the corrugated flute; its dormant options.filter_material_type_by could feed the
  // same shared filter as a follow-up. The /mcc picker does filter by flute.)
  const filterSystemItems = systemItemsFilter(config.key, { boardThicknessMm: options && options.material_thickness_in_mm });

  // The sheet count drives the price, so the material section surfaces it as its own disabled form field
  // beside the material picker, in the same 265px number-with-unit box the fire adeti field uses, with a
  // "sheets" addon so the unit is plain. It shows the bare count WITHOUT wastage (250, not the breakdown's
  // "250 + 110 = 360") -- taken from the priced response so it stays in step with the calculation, and gated
  // on a priced result so it never shows on its own without a price.
  const numberOfSheetsRow = (config.show_number_of_sheets_field && postOnChangeResponse != undefined && postOnChangeResponse.line_item_prices != undefined && postOnChangeResponse.line_item_prices.length > 0 && postOnChangeResponse.number_of_sheets_baseline != undefined) ? (
    <tr key="number_of_sheets_field">
      <td>
        {t('quote_builder.number_of_sheets')}
        {postOnChangeResponse.sheet_description != undefined && <> &nbsp; ({postOnChangeResponse.sheet_description}) </>}
      </td>
      <td align="right">
        <div style={{ display: 'inline-flex', alignItems: 'center', width: '265px' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}>
            <div className="input-group number_input" style={{ width: '100%' }}>
              <input type="text" className="form-control" size="10" style={{ width: '100%' }}
                     value={postOnChangeResponse.number_of_sheets_baseline} readOnly disabled />
              <span className="input-group-addon"><span> {t('quote_builder.wastage_unit_sheet')} </span></span>
            </div>
          </div>
        </div>
      </td>
    </tr>
  ) : null;

  // By default the sheet-count row sits after every attribute; a section may name an attribute in
  // number_of_sheets_before_field to place the row just above it instead (material puts it above fire adeti).
  const numberOfSheetsBeforeIndex = config.number_of_sheets_before_field != undefined
    ? config.attributes.findIndex((attribute) => attribute.name === config.number_of_sheets_before_field)
    : -1;

  return (
    <div ref={formRootRef} className={`${config.class_name != undefined ? config.class_name : ''} ${(fetchInProgress || disabled || localDisabled) ? 'disabledForm' : ''}`} style={{ ...(localHidden ? { display: 'none' } : {}), transition: 'opacity 0.2s ease', ...((disabled || localDisabled) ? {} : { opacity: resultsRefreshing ? 0.6 : 1 }) }} data-section-uuid={section_uuid} id={section_uuid}>
      {config.list.enabled && componentShowingState == STATE_LIST && <QuoteBuilderList config={config} setComponentShowingState={setComponentShowingState} setItemIdBeingEdited={setItemIdBeingEdited} hideDeleteFromList={hideDeleteFromList} filterSystemItems={filterSystemItems}/> }

      {editDataLoading && (
        <div className="quote-builder-form-loading" style={{ padding: '40px 20px', textAlign: 'center', color: '#888' }}>
          <i className="fa fa-spinner fa-spin fa-2x"></i>
          <div style={{ marginTop: '12px' }}>{t('quote_builder.please_wait')}</div>
        </div>
      )}

      {!editDataLoading && (componentShowingState == STATE_NEW || componentShowingState == STATE_EDIT || componentShowingState == STATE_COPY_FROM_SYSTEM) && (
        <Form
          onSubmit={handleSubmit(save, () => setSaveAttempted(true))}
          ref={myFormRef}
          onInput={noteUserChange}
          onKeyDown={(event) => { if (event.key === 'Enter') { commitNow(); } }}
          onBlur={(event) => { markFieldTouched(event.target.name); (config.post_on_change ? onBlurWrapped : validateOnChange)(event); }}>
         <table className="table table-striped table-bordered-outside quote_builder_table q-section-card">
          <tbody>
            <FormTitle 
              config={config}
              componentShowingState={componentShowingState}
              extra_title={extra_title}
              localDisabledReason={localDisabledReason || (disabled && disabledReason ? t(disabledReason) : null)}
              showLongDescriptions={showLongDescriptions}
              handleToggleLongDescriptions={handleToggleLongDescriptions}
            />

            {numberOfSheetsRow != null && numberOfSheetsBeforeIndex >= 0 ? (
              <>
                {renderAttributes(config.attributes.slice(0, numberOfSheetsBeforeIndex))}
                {numberOfSheetsRow}
                {renderAttributes(config.attributes.slice(numberOfSheetsBeforeIndex))}
              </>
            ) : (
              <>
                {renderAttributes(config.attributes)}
                {numberOfSheetsRow}
              </>
            )}

            <FormActions
              config={config}
              componentShowingState={componentShowingState}
              submitInProgress={submitInProgress}
              childSubFormOpen={childSubFormOpen}
              disabledIf={disabledIf}
              submit={submit}
              cancel={cancel}
              linkToSettings={linkToSettings}
            />

              {((config.post_on_change && postOnChangeResponse != undefined && postOnChangeResponse.line_item_prices != undefined && postOnChangeResponse.line_item_prices.length > 0) || inCompleteMessage || errorMessage) && (
                <tr>
                  <td> </td>
                  <td colSpan="1">
                     <ResultsTable 
                       config={config}
                       postOnChangeResponse={postOnChangeResponse}
                     />
                  </td>
                </tr>
              )}

            <SystemCheckbox 
              config={config}
              componentShowingState={componentShowingState}
              isSystem={isSystem}
              handleIsSystemChange={handleIsSystemChange}
            />

          </tbody>
        </table>

        <MessageAlerts 
          inCompleteMessage={inCompleteMessage}
          errorMessage={errorMessage}
        />

      </Form>
      )}
    </div>
  )
}

export default QuoteBuilderForm;
